import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Search, Plus, Trash2, X, Check, History, Tag, Filter, ChevronDown, Square, CheckSquare, AlertTriangle, Crown, Coins } from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDate } from '../../utils/timeUtils';
import { matchPinyinInitials } from '../../utils/pinyinInitials';
import TagManagerModal from '../../components/TagManagerModal';
import TeacherGiftPointsModal from '../../components/TeacherGiftPointsModal';
import StudentRedeemPointsModal from '../../components/StudentRedeemPointsModal';

interface Student {
  id: string;
  username: string;
  remark: string;
  studyCount30d: number;
  accuracy30d: number;
  lastStudyTime: string;
  tags: StudentTagInfo[];
}

interface StudentTagInfo {
  id: string;
  name: string;
  color: string;
}

interface StudyRecord {
  id: string;
  paper_name: string;
  score: number;
  total_questions: number;
  correct_count: number;
  accuracy: number;
  study_date: string;
}

interface StudentTag {
  id: string;
  name: string;
  color: string;
}

export default function TeacherStudentManagement() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string>('');
  const [students, setStudents] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showRecordsModal, setShowRecordsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showTagManager, setShowTagManager] = useState(false);
  const [showEditTagsModal, setShowEditTagsModal] = useState(false);
  const [editingStudentForTags, setEditingStudentForTags] = useState<Student | null>(null);
  const [editingRemark, setEditingRemark] = useState<{ studentId: string; value: string } | null>(null);
  const [studyRecords, setStudyRecords] = useState<StudyRecord[]>([]);
  const [newStudentPhone, setNewStudentPhone] = useState('');
  const [newStudentName, setNewStudentName] = useState('');
  const [newStudentRemark, setNewStudentRemark] = useState('');
  const [verifiedStudentId, setVerifiedStudentId] = useState<string | null>(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [availableTags, setAvailableTags] = useState<StudentTag[]>([]);
  const [selectedTagFilters, setSelectedTagFilters] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<'name' | 'tag' | 'studyCount' | 'accuracy' | 'lastStudy'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Gift points states
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftingStudent, setGiftingStudent] = useState<{id: string, username: string} | null>(null);
  const [teacherPoints, setTeacherPoints] = useState<number>(0);

  // Redeem points states
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [redeemingStudent, setRedeemingStudent] = useState<{id: string, username: string} | null>(null);

  // Batch operation states
  const [selectedStudents, setSelectedStudents] = useState<Set<string>>(new Set());
  const [showBatchTagDropdown, setShowBatchTagDropdown] = useState(false);
  const [batchOperationLoading, setBatchOperationLoading] = useState(false);
  const [showBatchDeleteConfirm, setShowBatchDeleteConfirm] = useState(false);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const { data: { session } } = await supabase.auth.getSession();
        setAccessToken(session?.access_token || '');
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (userId) {
      loadData();
      loadTags();
      loadTeacherPoints();
    }
  }, [userId]);

  const loadTags = async () => {
    if (!userId) return;

    const { data } = await supabase
      .from('teacher_student_tags')
      .select('*')
      .eq('teacher_id', userId)
      .order('sort_order', { ascending: true });

    setAvailableTags(data || []);
  };

  // 加载教师U币余额
  const loadTeacherPoints = async () => {
    if (!userId) return;
    const { data } = await supabase
      .from('user_point_accounts')
      .select('total_points')
      .eq('user_id', userId)
      .eq('role', 'teacher')
      .maybeSingle();
    setTeacherPoints(data?.total_points || 0);
  };

  // 打开赠送弹窗
  const openGiftModal = (student: Student) => {
    setGiftingStudent({ id: student.id, username: student.username });
    setShowGiftModal(true);
  };

  // 赠送成功回调
  const handleGiftSuccess = () => {
    setShowGiftModal(false);
    setGiftingStudent(null);
    setMessage({ type: 'success', text: 'U币赠送成功' });
    // 刷新教师余额
    loadTeacherPoints();
  };

  // 打开兑换弹窗
  const openRedeemModal = (student: Student) => {
    setRedeemingStudent({ id: student.id, username: student.username });
    setShowRedeemModal(true);
  };

  // 兑换成功回调
  const handleRedeemSuccess = () => {
    setShowRedeemModal(false);
    setRedeemingStudent(null);
    setMessage({ type: 'success', text: 'U币兑换成功' });
    // 刷新学员列表
    loadData();
  };

  const loadData = async () => {
    if (!userId) return;
    setLoading(true);

    const teacherId = userId;

    const { data: teacherStudents } = await supabase
      .from('teacher_students')
      .select('student_id, remark')
      .eq('teacher_id', teacherId);

    const studentIds = teacherStudents?.map(s => s.student_id) || [];

    if (studentIds.length === 0) {
      setStudents([]);
      setLoading(false);
      return;
    }

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();

    // 并行查询所有数据，大幅减少等待时间
    const [profilesResult, examRecordsResult, studentAssignmentsResult, tagsResult, tagRelationsResult] = await Promise.all([
      supabase.from('profiles').select('id, username, phone, avatar_seed').in('id', studentIds),
      supabase.from('exam_records').select('id, user_id, paper_id, created_at, objective_score, objective_total_score').in('user_id', studentIds).gte('created_at', thirtyDaysAgoStr),
      supabase.from('student_assignments').select('exam_record_id, assignment_id, student_id, teacher_assignments!inner(assignment_type)').in('student_id', studentIds).not('exam_record_id', 'is', null),
      supabase.from('teacher_student_tags').select('*').eq('teacher_id', teacherId),
      supabase.from('teacher_student_tag_relations').select('*').eq('teacher_id', teacherId).in('student_id', studentIds),
    ]);

    const profiles = profilesResult.data;
    const examRecords = examRecordsResult.data;
    const studentAssignments = studentAssignmentsResult.data;
    const tagsData = tagsResult.data;
    const tagRelations = tagRelationsResult.data;

    // 构建 exam_record_id -> assignment_type 的映射
    const assignmentTypeMap: Record<string, string> = {};
    if (studentAssignments) {
      for (const sa of studentAssignments) {
        if (sa.exam_record_id) {
          const assignmentType = sa.teacher_assignments?.assignment_type || 'class';
          assignmentTypeMap[sa.exam_record_id] = assignmentType;
        }
      }
    }

    const studentList: Student[] = (profiles || []).map(profile => {
      const studentExamRecords = examRecords?.filter(r => r.user_id === profile.id) || [];
      const studyCount30d = studentExamRecords.length;

      // 只统计课堂作业和考级竞赛的首次答题正确率
      // 1. 筛选出来源为课堂作业(class)或考级竞赛(exam)的记录
      // 2. 对同一份试卷(paper_id)，只取首次答题记录（按时间最早）
      const assignmentRecords = studentExamRecords.filter(r => {
        const assignmentType = assignmentTypeMap[r.id];
        return assignmentType === 'class' || assignmentType === 'competition' || assignmentType === 'exam';
      });

      // 按试卷分组，每组只取最早的一条（首次答题）
      const firstAttemptMap = new Map<string, any>();
      for (const r of assignmentRecords) {
        const key = r.paper_id;
        if (!firstAttemptMap.has(key) || new Date(r.created_at) < new Date(firstAttemptMap.get(key).created_at)) {
          firstAttemptMap.set(key, r);
        }
      }
      const firstAttemptRecords = Array.from(firstAttemptMap.values());

      const avgAccuracy = firstAttemptRecords.length > 0
        ? Math.round(firstAttemptRecords.reduce((sum, r) => {
            // 只计算客观题正确率
            const objectiveScore = r.objective_score || 0;
            const objectiveMaxScore = r.objective_total_score || 0;

            return sum + (objectiveMaxScore > 0 ? (objectiveScore / objectiveMaxScore * 100) : 0);
          }, 0) / firstAttemptRecords.length)
        : 0;
      const teacherStudent = teacherStudents?.find(s => s.student_id === profile.id);
      const remark = teacherStudent?.remark || '';

      // Get all tags for this student
      const studentTagRelations = tagRelations?.filter(tr => tr.student_id === profile.id) || [];
      const studentTags: StudentTagInfo[] = studentTagRelations.map(tr => {
        const tag = tagsData?.find(t => t.id === tr.tag_id);
        return tag ? { id: tag.id, name: tag.name, color: tag.color } : null;
      }).filter(Boolean) as StudentTagInfo[];

      const lastExamRecord = studentExamRecords.sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )[0];
      const lastStudyTime = lastExamRecord ? formatDate(lastExamRecord.created_at) : '-';

      return {
        id: profile.id,
        username: profile.username,
        remark,
        studyCount30d,
        accuracy30d: avgAccuracy,
        lastStudyTime,
        tags: studentTags,
      };
    });

    setStudents(studentList);
    setLoading(false);
  };

  // Batch operation handlers
  const toggleSelectAll = () => {
    if (selectedStudents.size === filteredStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(filteredStudents.map(s => s.id)));
    }
  };

  const toggleSelectStudent = (studentId: string) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const handleBatchAddTag = async (tagId: string) => {
    if (selectedStudents.size === 0 || !tagId) return;
    setBatchOperationLoading(true);

    if (!userId) {
      setBatchOperationLoading(false);
      return;
    }

    // 获取要添加的标签信息
    const tagToAdd = availableTags.find(t => t.id === tagId);
    if (!tagToAdd) {
      setBatchOperationLoading(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const studentId of selectedStudents) {
      const { error } = await supabase
        .from('teacher_student_tag_relations')
        .upsert({
          teacher_id: userId,
          student_id: studentId,
          tag_id: tagId,
        }, { onConflict: 'teacher_id,student_id,tag_id' });

      if (error) {
        failCount++;
      } else {
        successCount++;
      }
    }

    // 本地状态更新 - 不重新加载所有数据
    if (successCount > 0) {
      setStudents(prev => prev.map(student => {
        if (selectedStudents.has(student.id)) {
          // 检查标签是否已存在
          const hasTag = student.tags.some(t => t.id === tagId);
          if (!hasTag) {
            return {
              ...student,
              tags: [...student.tags, { id: tagToAdd.id, name: tagToAdd.name, color: tagToAdd.color }]
            };
          }
        }
        return student;
      }));
    }

    setBatchOperationLoading(false);
    setShowBatchTagDropdown(false);
    setSelectedStudents(new Set());

    if (failCount === 0) {
      setMessage({ type: 'success', text: `成功为 ${successCount} 位学员添加标签` });
    } else {
      setMessage({ type: 'error', text: `成功 ${successCount} 位，失败 ${failCount} 位` });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleBatchRemoveTag = async (tagId: string) => {
    if (selectedStudents.size === 0 || !tagId) return;
    setBatchOperationLoading(true);

    if (!userId) {
      setBatchOperationLoading(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const studentId of selectedStudents) {
      const { error } = await supabase
        .from('teacher_student_tag_relations')
        .delete()
        .eq('teacher_id', userId)
        .eq('student_id', studentId)
        .eq('tag_id', tagId);

      if (error) {
        failCount++;
      } else {
        successCount++;
      }
    }

    // 本地状态更新 - 不重新加载所有数据
    if (successCount > 0) {
      setStudents(prev => prev.map(student => {
        if (selectedStudents.has(student.id)) {
          return {
            ...student,
            tags: student.tags.filter(t => t.id !== tagId)
          };
        }
        return student;
      }));
    }

    setBatchOperationLoading(false);
    setShowBatchTagDropdown(false);
    setSelectedStudents(new Set());

    if (failCount === 0) {
      setMessage({ type: 'success', text: `成功从 ${successCount} 位学员移除标签` });
    } else {
      setMessage({ type: 'error', text: `成功 ${successCount} 位，失败 ${failCount} 位` });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleBatchDelete = async () => {
    if (selectedStudents.size === 0) return;
    setBatchOperationLoading(true);

    if (!userId) {
      setBatchOperationLoading(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const studentId of selectedStudents) {
      try {
        const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ action: 'unbind', studentId }),
        });
        const result = await response.json();
        if (result.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }

    setBatchOperationLoading(false);
    setShowBatchDeleteConfirm(false);
    setSelectedStudents(new Set());
    window.dispatchEvent(new CustomEvent('teacher-package-updated'));
    await loadData();

    if (failCount === 0) {
      setMessage({ type: 'success', text: `成功删除 ${successCount} 位学员` });
    } else {
      setMessage({ type: 'error', text: `成功 ${successCount} 位，失败 ${failCount} 位` });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleViewRecords = async (student: Student) => {
    setSelectedStudent(student);
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    const sixMonthsAgoStr = `${sixMonthsAgo.getFullYear()}-${String(sixMonthsAgo.getMonth() + 1).padStart(2, '0')}-${String(sixMonthsAgo.getDate()).padStart(2, '0')}`;

    if (!userId) return;

    const { data } = await supabase
      .from('exam_records')
      .select('*')
      .eq('user_id', student.id)
      .gte('created_at', sixMonthsAgoStr)
      .order('created_at', { ascending: false });

    setStudyRecords((data || []) as unknown as StudyRecord[]);
    setShowRecordsModal(true);
  };

  const handleVerifyStudent = async () => {
    if (!newStudentPhone || !newStudentName) {
      setMessage({ type: 'error', text: '请填写学员手机号和姓名' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    if (!/^1[3-9]\d{9}$/.test(newStudentPhone)) {
      setMessage({ type: 'error', text: '请输入正确的手机号格式' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    try {
      const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

      const response = await fetch(`${(window as any).MEOO_CONFIG?.meoo_app_access_url || location.origin}/sb-api/functions/v1/edu-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ action: 'verify_student', phone: newStudentPhone, username: newStudentName }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        setMessage({ type: 'error', text: result.message || '校验失败' });
        setVerifiedStudentId(null);
      } else {
        setMessage({ type: 'success', text: '校验通过，请点击确认添加' });
        setVerifiedStudentId(result.studentId);
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: '校验失败：' + error.message });
      setVerifiedStudentId(null);
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleAddStudent = async () => {
    if (!verifiedStudentId) {
      setMessage({ type: 'error', text: '请先进行校验' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return;
    }

    if (!userId) return;

    const { error } = await supabase
      .from('teacher_students')
      .insert({
        teacher_id: userId,
        student_id: verifiedStudentId,
        remark: newStudentRemark,
      });

    if (error) {
      setMessage({ type: 'error', text: '添加失败，请稍后重试' });
    } else {
      setMessage({ type: 'success', text: '添加成功' });
      setShowAddModal(false);
      setNewStudentPhone('');
      setNewStudentName('');
      setNewStudentRemark('');
      setVerifiedStudentId(null);
      loadData();
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleDeleteStudent = async (studentId: string) => {
    if (!userId) return;

    try {
      const response = await fetch(`${getSupabaseUrl()}/functions/v1/edu-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ action: 'unbind', studentId }),
      });
      const result = await response.json();
      if (result.success) {
        setMessage({ type: 'success', text: '删除学员成功' });
        window.dispatchEvent(new CustomEvent('teacher-package-updated'));
        await loadData();
      } else {
        setMessage({ type: 'error', text: result.message || '删除失败' });
      }
    } catch {
      setMessage({ type: 'error', text: '删除失败，请稍后重试' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleSaveRemark = async (studentId: string) => {
    if (!editingRemark) return;
    if (!userId) return;

    const { error } = await supabase
      .from('teacher_students')
      .update({ remark: editingRemark.value })
      .eq('teacher_id', userId)
      .eq('student_id', studentId);

    if (!error) {
      setEditingRemark(null);
      loadData();
      setMessage({ type: 'success', text: '备注已更新' });
    } else {
      setMessage({ type: 'error', text: '更新失败' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleAddTagToStudent = async (studentId: string, tagId: string) => {
    if (!userId || !tagId) return;

    // 获取要添加的标签信息
    const tagToAdd = availableTags.find(t => t.id === tagId);
    if (!tagToAdd) return;

    const { error } = await supabase
      .from('teacher_student_tag_relations')
      .upsert({
        teacher_id: userId,
        student_id: studentId,
        tag_id: tagId,
      }, { onConflict: 'teacher_id,student_id,tag_id' });

    if (!error) {
      // 本地状态更新 - 不重新加载所有数据
      setStudents(prev => prev.map(student => {
        if (student.id === studentId) {
          const hasTag = student.tags.some(t => t.id === tagId);
          if (!hasTag) {
            return {
              ...student,
              tags: [...student.tags, { id: tagToAdd.id, name: tagToAdd.name, color: tagToAdd.color }]
            };
          }
        }
        return student;
      }));
      // 同步更新编辑中的学员
      setEditingStudentForTags(prev => {
        if (!prev || prev.id !== studentId) return prev;
        const hasTag = prev.tags.some(t => t.id === tagId);
        if (hasTag) return prev;
        return {
          ...prev,
          tags: [...prev.tags, { id: tagToAdd.id, name: tagToAdd.name, color: tagToAdd.color }]
        };
      });
      setMessage({ type: 'success', text: '标签已添加' });
    } else {
      setMessage({ type: 'error', text: '添加标签失败' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleRemoveTagFromStudent = async (studentId: string, tagId: string) => {
    if (!userId) return;

    const { error } = await supabase
      .from('teacher_student_tag_relations')
      .delete()
      .eq('teacher_id', userId)
      .eq('student_id', studentId)
      .eq('tag_id', tagId);

    if (!error) {
      // 本地状态更新 - 不重新加载所有数据
      setStudents(prev => prev.map(student => {
        if (student.id === studentId) {
          return {
            ...student,
            tags: student.tags.filter(t => t.id !== tagId)
          };
        }
        return student;
      }));
      // 同步更新编辑中的学员
      setEditingStudentForTags(prev => {
        if (!prev || prev.id !== studentId) return prev;
        return {
          ...prev,
          tags: prev.tags.filter(t => t.id !== tagId)
        };
      });
      setMessage({ type: 'success', text: '标签已移除' });
    } else {
      setMessage({ type: 'error', text: '移除标签失败' });
    }
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const openEditTagsModal = (student: Student) => {
    setEditingStudentForTags(student);
    setShowEditTagsModal(true);
  };

  const handleSort = (field: 'name' | 'tag' | 'studyCount' | 'accuracy' | 'lastStudy') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortedStudents = (students: Student[]) => {
    const sorted = [...students];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.username.localeCompare(b.username);
          break;
        case 'tag':
          const tagA = a.tags[0]?.name || '';
          const tagB = b.tags[0]?.name || '';
          comparison = tagA.localeCompare(tagB);
          break;
        case 'studyCount':
          comparison = a.studyCount30d - b.studyCount30d;
          break;
        case 'accuracy':
          comparison = a.accuracy30d - b.accuracy30d;
          break;
        case 'lastStudy':
          const dateA = a.lastStudyTime === '-' ? '0000-00-00' : a.lastStudyTime;
          const dateB = b.lastStudyTime === '-' ? '0000-00-00' : b.lastStudyTime;
          comparison = dateA.localeCompare(dateB);
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return sorted;
  };

  const filteredStudents = getSortedStudents(
    (students || []).filter(s => {
      const matchesSearch = matchPinyinInitials(s.username, searchQuery) || matchPinyinInitials(s.remark, searchQuery);
      // 多标签筛选：只要学员拥有任一选中标签即匹配
      const matchesTag = selectedTagFilters.length === 0 ||
        s.tags.some(tag => selectedTagFilters.includes(tag.id));
      return matchesSearch && matchesTag;
    })
  );

  const isAllSelected = filteredStudents.length > 0 && selectedStudents.size === filteredStudents.length;
  const isPartialSelected = selectedStudents.size > 0 && selectedStudents.size < filteredStudents.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">学员管理</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowTagManager(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
          >
            <Tag className="w-4 h-4" />
            标签管理
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            添加学员
          </button>
        </div>
      </div>

      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-3 rounded-lg ${
            message.type === 'success' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
          }`}
        >
          {message.text}
        </motion.div>
      )}

      {/* Batch Operation Toolbar */}
      <AnimatePresence>
        {selectedStudents.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <span className="text-purple-700 font-medium">
                已选择 {selectedStudents.size} 位学员
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* Batch Add Tag */}
              <div className="relative">
                <button
                  onClick={() => setShowBatchTagDropdown(!showBatchTagDropdown)}
                  disabled={batchOperationLoading}
                  className="flex items-center gap-2 px-3 py-2 bg-white text-purple-700 rounded-lg hover:bg-purple-100 transition-colors border border-purple-200"
                >
                  <Tag className="w-4 h-4" />
                  标签批量操作
                  <ChevronDown className="w-4 h-4" />
                </button>
                {showBatchTagDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 min-w-[180px]">
                    <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">点击添加标签</div>
                    {availableTags.map(tag => (
                      <button
                        key={tag.id}
                        onClick={() => handleBatchAddTag(tag.id)}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                      >
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </button>
                    ))}
                    <div className="border-t border-gray-100 mt-1">
                      <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">点击移除标签</div>
                      <button
                        onClick={async () => {
                          if (selectedStudents.size === 0) return;
                          setBatchOperationLoading(true);

                          if (!userId) {
                            setBatchOperationLoading(false);
                            return;
                          }

                          // 获取选中学员当前的所有标签
                          const selectedStudentIds = Array.from(selectedStudents);
                          let totalRemoved = 0;
                          let failCount = 0;

                          for (const studentId of selectedStudentIds) {
                            const student = students.find(s => s.id === studentId);
                            if (!student || student.tags.length === 0) continue;

                            for (const tag of student.tags) {
                              const { error } = await supabase
                                .from('teacher_student_tag_relations')
                                .delete()
                                .eq('teacher_id', userId)
                                .eq('student_id', studentId)
                                .eq('tag_id', tag.id);

                              if (error) {
                                failCount++;
                              } else {
                                totalRemoved++;
                              }
                            }
                          }

                          // 本地状态更新
                          if (totalRemoved > 0) {
                            setStudents(prev => prev.map(student => {
                              if (selectedStudents.has(student.id)) {
                                return { ...student, tags: [] };
                              }
                              return student;
                            }));
                          }

                          setBatchOperationLoading(false);
                          setShowBatchTagDropdown(false);
                          setSelectedStudents(new Set());

                          if (failCount === 0) {
                            setMessage({ type: 'success', text: `成功从 ${selectedStudentIds.length} 位学员移除所有标签` });
                          } else {
                            setMessage({ type: 'error', text: `成功移除 ${totalRemoved} 个标签，失败 ${failCount} 个` });
                          }
                          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                        }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2 border-b border-gray-100"
                      >
                        <Trash2 className="w-3 h-3" />
                        移除全部标签
                      </button>
                      {availableTags.map(tag => (
                        <button
                          key={`remove-${tag.id}`}
                          onClick={() => handleBatchRemoveTag(tag.id)}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                        >
                          <X className="w-3 h-3" />
                          移除 {tag.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Batch Delete */}
              <button
                onClick={() => setShowBatchDeleteConfirm(true)}
                disabled={batchOperationLoading}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                批量删除学员
              </button>

              {/* Clear Selection */}
              <button
                onClick={() => setSelectedStudents(new Set())}
                className="px-3 py-2 text-gray-500 hover:text-gray-700"
              >
                取消选择
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="搜索学员姓名、备注或拼音首字母（如 zs 匹配张三）"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 outline-none"
              />
            </div>
          </div>
          {/* 多标签筛选 */}
          {availableTags.length > 0 && (
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">标签筛选：</span>
              <div className="flex flex-wrap gap-2">
                {availableTags.map(tag => {
                  const isSelected = selectedTagFilters.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => {
                        setSelectedTagFilters(prev =>
                          prev.includes(tag.id)
                            ? prev.filter(id => id !== tag.id)
                            : [...prev, tag.id]
                        );
                      }}
                      className={`px-3 py-1 text-sm rounded-full border-2 transition-all ${
                        isSelected
                          ? 'border-transparent text-white'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300 bg-white'
                      }`}
                      style={isSelected ? { backgroundColor: tag.color } : {}}
                    >
                      {tag.name}
                    </button>
                  );
                })}
                {selectedTagFilters.length > 0 && (
                  <button
                    onClick={() => setSelectedTagFilters([])}
                    className="px-3 py-1 text-sm text-gray-500 hover:text-red-600"
                  >
                    清空筛选
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button
                    onClick={toggleSelectAll}
                    className="flex items-center gap-2 text-xs font-medium text-gray-500 hover:text-gray-700"
                  >
                    {isAllSelected ? (
                      <CheckSquare className="w-4 h-4 text-purple-600" />
                    ) : isPartialSelected ? (
                      <div className="w-4 h-4 border-2 border-purple-600 bg-purple-600 rounded flex items-center justify-center">
                        <div className="w-2 h-0.5 bg-white"></div>
                      </div>
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                    全选
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                  学员姓名 {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">标签</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">备注</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('lastStudy')}>
                  最近刷题时间 {sortBy === 'lastStudy' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('studyCount')}>
                  近30天刷题次数 {sortBy === 'studyCount' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('accuracy')}>
                  近30天客观题正确率 {sortBy === 'accuracy' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">答题记录</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleSelectStudent(student.id)}
                      className="flex items-center"
                    >
                      {selectedStudents.has(student.id) ? (
                        <CheckSquare className="w-4 h-4 text-purple-600" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-800">{student.username?.includes('@') ? student.username.split('@')[0] : student.username}</td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-1 flex-wrap">
                      {student.tags.length > 0 ? (
                        student.tags.map(tag => (
                          <span
                            key={tag.id}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: tag.color }}
                          >
                            {tag.name}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveTagFromStudent(student.id, tag.id);
                              }}
                              className="ml-1 hover:opacity-70"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))
                      ) : null}
                      <button
                        onClick={() => openEditTagsModal(student)}
                        className="text-gray-400 text-xs hover:text-purple-600 px-1 py-0.5 rounded hover:bg-purple-50"
                      >
                        + 管理标签
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {editingRemark?.studentId === student.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingRemark.value}
                          onChange={(e) => setEditingRemark({ ...editingRemark, value: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRemark(student.id);
                            if (e.key === 'Escape') setEditingRemark(null);
                          }}
                          className="w-32 px-2 py-1 text-sm border border-purple-300 rounded focus:ring-2 focus:ring-purple-500 outline-none"
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveRemark(student.id)}
                          className="p-1 text-green-600 hover:bg-green-50 rounded"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingRemark(null)}
                          className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span
                        onClick={() => setEditingRemark({ studentId: student.id, value: student.remark })}
                        className="cursor-pointer hover:text-purple-600 hover:underline"
                        title="点击编辑备注"
                      >
                        {student.remark || '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{student.lastStudyTime}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{student.studyCount30d}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {student.accuracy30d > 0 ? `${student.accuracy30d}%` : '-'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => navigate(`/exam-records?studentId=${student.id}&returnUrl=/teacher/students`)}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                    >
                      <History className="w-4 h-4" />
                      查看
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openGiftModal(student)}
                        className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                        title="奖励U币"
                      >
                        <Crown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openRedeemModal(student)}
                        className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                        title="U币兑换"
                      >
                        <Coins className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteStudent(student.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="删除学员"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredStudents.length === 0 && (
          <div className="p-8 text-center text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>暂无学员数据</p>
          </div>
        )}
      </div>

      {/* Tag Manager Modal */}
      <TagManagerModal isOpen={showTagManager} onClose={() => { setShowTagManager(false); loadTags(); }} />

      {/* Edit Tags Modal */}
      <AnimatePresence>
        {showEditTagsModal && editingStudentForTags && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl p-6 w-96 max-h-[80vh] overflow-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  管理标签 - {editingStudentForTags.username}
                </h3>
                <button onClick={() => setShowEditTagsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">当前标签</h4>
                    {editingStudentForTags.tags.length > 0 && (
                      <button
                        onClick={async () => {
                          if (!userId) return;

                          let successCount = 0;
                          let failCount = 0;
                          const studentId = editingStudentForTags.id;
                          const tagsToRemove = [...editingStudentForTags.tags];

                          for (const tag of tagsToRemove) {
                            const { error } = await supabase
                              .from('teacher_student_tag_relations')
                              .delete()
                              .eq('teacher_id', userId)
                              .eq('student_id', studentId)
                              .eq('tag_id', tag.id);

                            if (error) {
                              failCount++;
                            } else {
                              successCount++;
                            }
                          }

                          if (successCount > 0) {
                            setStudents(prev => prev.map(student => {
                              if (student.id === studentId) {
                                return { ...student, tags: [] };
                              }
                              return student;
                            }));
                            setEditingStudentForTags(prev => {
                              if (!prev) return prev;
                              return { ...prev, tags: [] };
                            });
                            setMessage({ type: 'success', text: `成功移除 ${successCount} 个标签` });
                          } else if (failCount > 0) {
                            setMessage({ type: 'error', text: '移除标签失败' });
                          }
                          setTimeout(() => setMessage({ type: '', text: '' }), 3000);
                        }}
                        className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded transition-colors"
                      >
                        移除全部
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {editingStudentForTags.tags.length > 0 ? (
                      editingStudentForTags.tags.map(tag => (
                        <span
                          key={tag.id}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm text-white"
                          style={{ backgroundColor: tag.color }}
                        >
                          {tag.name}
                          <button
                            onClick={() => handleRemoveTagFromStudent(editingStudentForTags.id, tag.id)}
                            className="ml-2 hover:opacity-70"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-400 text-sm">暂无标签</span>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">添加标签</h4>
                  <div className="flex flex-wrap gap-2">
                    {availableTags
                      .filter(tag => !editingStudentForTags.tags.some(t => t.id === tag.id))
                      .map(tag => (
                        <button
                          key={tag.id}
                          onClick={() => handleAddTagToStudent(editingStudentForTags.id, tag.id)}
                          className="px-3 py-1 rounded-full text-sm border-2 border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50 transition-all"
                        >
                          + {tag.name}
                        </button>
                      ))}
                    {availableTags.filter(tag => !editingStudentForTags.tags.some(t => t.id === tag.id)).length === 0 && (
                      <span className="text-gray-400 text-sm">所有标签已添加</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowEditTagsModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  完成
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Batch Delete Confirm Modal */}
      <AnimatePresence>
        {showBatchDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl p-6 w-96"
            >
              <div className="flex items-center gap-3 mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <h3 className="text-lg font-bold text-gray-800">确认批量删除</h3>
              </div>
              <p className="text-gray-600 mb-6">
                确定要删除选中的 <span className="font-bold text-red-600">{selectedStudents.size}</span> 位学员吗？<br/>
                此操作不可恢复。
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBatchDeleteConfirm(false)}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={handleBatchDelete}
                  disabled={batchOperationLoading}
                  className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 flex items-center justify-center gap-2"
                >
                  {batchOperationLoading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  确认删除
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRecordsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl p-6 w-[600px] max-h-[80vh] overflow-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  {selectedStudent?.username} - 答题记录
                </h3>
                <button onClick={() => setShowRecordsModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              {studyRecords.length > 0 ? (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">试卷</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">得分</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">正确率</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">日期</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {studyRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2 text-sm text-gray-800">{record.paper_name || '未知试卷'}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{record.score}/{record.total_questions}</td>
                        <td className="px-4 py-2 text-sm text-green-600">{record.accuracy}%</td>
                        <td className="px-4 py-2 text-sm text-gray-500">{record.study_date}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center text-gray-400 py-8">近6个月暂无答题记录</p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white rounded-xl p-6 w-96"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">添加学员</h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">学员手机号</label>
                  <input
                    type="text"
                    value={newStudentPhone}
                    onChange={(e) => {
                      setNewStudentPhone(e.target.value);
                      setVerifiedStudentId(null);
                    }}
                    placeholder="请输入学员注册手机号"
                    maxLength={11}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">学员姓名</label>
                  <input
                    type="text"
                    value={newStudentName}
                    onChange={(e) => {
                      setNewStudentName(e.target.value);
                      setVerifiedStudentId(null);
                    }}
                    placeholder="请输入学员姓名"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">备注</label>
                  <input
                    type="text"
                    value={newStudentRemark}
                    onChange={(e) => setNewStudentRemark(e.target.value)}
                    placeholder="可选"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  />
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewStudentPhone('');
                    setNewStudentName('');
                    setNewStudentRemark('');
                    setVerifiedStudentId(null);
                  }}
                  className="flex-1 py-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={handleVerifyStudent}
                  disabled={!newStudentPhone || !newStudentName || verifiedStudentId !== null}
                  className={`flex-1 py-2 rounded-lg transition-colors ${
                    verifiedStudentId
                      ? 'bg-green-600 text-white cursor-default'
                      : 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-400'
                  }`}
                >
                  {verifiedStudentId ? '已校验' : '校验'}
                </button>
                <button
                  onClick={handleAddStudent}
                  disabled={!verifiedStudentId}
                  className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
                >
                  确认添加
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 奖励U币弹窗 */}
      {showGiftModal && giftingStudent && (
        <TeacherGiftPointsModal
          isOpen={showGiftModal}
          onClose={() => { setShowGiftModal(false); setGiftingStudent(null); }}
          student={giftingStudent}
          teacherId={userId!}
          onSuccess={handleGiftSuccess}
        />
      )}

      {/* U币兑换弹窗 */}
      {showRedeemModal && redeemingStudent && (
        <StudentRedeemPointsModal
          isOpen={showRedeemModal}
          onClose={() => { setShowRedeemModal(false); setRedeemingStudent(null); }}
          student={redeemingStudent}
          teacherId={userId!}
          onSuccess={handleRedeemSuccess}
        />
      )}

    </div>
  );
}
